/**
 * Plan Comparison (PRD 10.2, FR-205).
 *
 * Side-by-side cost, TG fit, eOTS, coverage and assumptions, with the asset
 * churn and a plain-language trade-off summary. A feature metric shows its
 * data class in every column, because comparing a measured number with a
 * modelled one is exactly the mistake this screen has to prevent.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PlanComparison as Comparison, PlanResponse } from '../lib/types'
import { moneyShort, percent } from '../lib/format'
import { Callout, EmptyState, ErrorCallout } from '../components/Callout'
import { DataClassChip } from '../components/Chips'
import { Skeleton } from '../components/Metric'

export function PlanCompare() {
  const { planId } = useParams()
  const navigate = useNavigate()
  const [selected, setSelected] = useState<number[]>([])

  const plan = useQuery<PlanResponse>({
    queryKey: ['plan', planId],
    queryFn: () => api.getPlan(planId!),
    enabled: Boolean(planId),
  })

  // Default to the two most recent versions, which is the common case.
  useEffect(() => {
    if (plan.data && selected.length === 0 && plan.data.versions.length >= 2) {
      setSelected(plan.data.versions.slice(-2).map((v) => v.version))
    }
  }, [plan.data, selected.length])

  const comparison = useQuery<Comparison>({
    queryKey: ['compare', planId, selected],
    queryFn: () => api.compare(planId!, selected),
    enabled: Boolean(planId) && selected.length >= 2,
  })

  if (plan.isLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={5} />
        </div>
      </main>
    )
  }

  const versions = plan.data?.versions ?? []
  if (versions.length < 2) {
    return (
      <main className="main">
        <div className="page">
          <EmptyState
            title="Two versions are needed to compare"
            action={
              <button className="primary" onClick={() => navigate(`/plans/${planId}`)}>
                Back to the plan
              </button>
            }
          >
            Refine the plan to create another version.
          </EmptyState>
        </div>
      </main>
    )
  }

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Compare versions</h1>
            <div className="page-sub">{plan.data?.name}</div>
          </div>
          <button onClick={() => navigate(`/plans/${planId}`)}>Back to the plan</button>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Versions to compare</h2>
          </div>
          <div className="card-body pill-row">
            {versions.map((v) => {
              const on = selected.includes(v.version)
              return (
                <button
                  key={v.version}
                  className={`small ${on ? 'primary' : ''}`}
                  onClick={() =>
                    setSelected((prev) =>
                      on ? prev.filter((n) => n !== v.version) : [...prev, v.version].sort((a, b) => a - b),
                    )
                  }
                >
                  v{v.version}
                  {plan.data?.approved_version === v.version ? ' ✓' : ''}
                  <span className="faint"> · {v.asset_count} assets</span>
                </button>
              )
            })}
          </div>
        </div>

        {selected.length < 2 ? (
          <Callout tone="info" title="Pick at least two versions" />
        ) : comparison.isLoading ? (
          <Skeleton rows={6} />
        ) : comparison.isError ? (
          <ErrorCallout error={comparison.error} onRetry={() => void comparison.refetch()} />
        ) : comparison.data ? (
          <>
            {comparison.data.trade_offs.length ? (
              <div className="card">
                <div className="card-head">
                  <h2 className="grow">What changed</h2>
                </div>
                <div className="card-body stack tight">
                  {comparison.data.trade_offs.map((line, i) => (
                    <div key={i} className="small">
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="card">
              <div className="card-head">
                <h2 className="grow">Metrics</h2>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      {comparison.data.versions.map((v) => (
                        <th key={v} className="num">
                          v{v}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(comparison.data.metric_diff).map(([key, row]) => (
                      <tr key={key}>
                        <td className="small">{row.label}</td>
                        {comparison.data!.versions.map((v) => {
                          const cell = row.values[String(v)]
                          return (
                            <td key={v} className="num tnum nowrap">
                              {renderCell(row.kind, cell)}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid two">
              <div className="card">
                <div className="card-head">
                  <h2 className="grow">Assets added</h2>
                </div>
                <div className="card-body stack tight">
                  {Object.entries(comparison.data.added_assets).map(([step, names]) => (
                    <div key={step}>
                      <div className="tiny faint">{step}</div>
                      {names.length ? (
                        <ul style={{ margin: '2px 0 0', paddingLeft: 18 }} className="small">
                          {names.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="small muted">none</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-head">
                  <h2 className="grow">Assets removed</h2>
                </div>
                <div className="card-body stack tight">
                  {Object.entries(comparison.data.removed_assets).map(([step, names]) => (
                    <div key={step}>
                      <div className="tiny faint">{step}</div>
                      {names.length ? (
                        <ul style={{ margin: '2px 0 0', paddingLeft: 18 }} className="small">
                          {names.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="small muted">none</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="grow">Assumptions per version</h2>
              </div>
              <div className="card-body grid two">
                {Object.entries(comparison.data.assumption_diff).map(([version, statements]) => (
                  <div key={version} className="stack tight">
                    <h4>v{version}</h4>
                    <ul style={{ margin: 0, paddingLeft: 18 }} className="small">
                      {statements.map((statement, i) => (
                        <li key={i}>{statement}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </main>
  )
}

function renderCell(kind: string, cell: unknown): React.ReactNode {
  if (cell === null || cell === undefined) return <span className="faint">—</span>
  if (kind === 'feature') {
    const feature = cell as { value: number | null; data_class: string; unit: string | null }
    if (feature.value === null) return <span className="faint">Unavailable</span>
    return (
      <div className="row tight" style={{ justifyContent: 'flex-end' }}>
        <span>{formatByUnit(feature.value, feature.unit)}</span>
        <DataClassChip value={feature.data_class as any} />
      </div>
    )
  }
  if (kind === 'minor') return moneyShort(Number(cell))
  if (kind === 'share') return percent(Number(cell), 1)
  if (kind === 'text') return <span className="small">{String(cell).replace(/_/g, ' ')}</span>
  if (typeof cell === 'number') return cell.toLocaleString('en-IN', { maximumFractionDigits: 2 })
  return String(cell)
}

function formatByUnit(value: number, unit: string | null): string {
  if (unit === 'index_0_1' || unit === 'share_0_1') return percent(value, 1)
  if (unit === 'minor_units_per_1000_target_impressions') return moneyShort(value)
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)
}
